import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, DollarSign, User, Phone, Mail, MapPin, FileText, Wrench, Package, AlertTriangle, Repeat, Crown } from 'lucide-react';
import { format, addMonths } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECURRENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
];

export default function NewJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [jobLimits, setJobLimits] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Recurring options
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  
  // Pre-fill from client if navigated from client page
  const clientFromState = location.state?.client;
  
  const [formData, setFormData] = useState({
    client_name: clientFromState?.name || '',
    client_phone: clientFromState?.phone || '',
    client_email: clientFromState?.email || '',
    client_id: clientFromState?.id || '',
    address_street: clientFromState?.address_street || '',
    address_city: clientFromState?.address_city || '',
    address_state: clientFromState?.address_state || '',
    address_zip: clientFromState?.address_zip || '',
    description: '',
    labor_amount: '',
    materials_amount: ''
  });
  
  const hasRecurring = jobLimits?.plan_limits?.has_recurring ?? (user?.subscription_tier === 'crew');

  useEffect(() => {
    fetchJobLimits();
  }, []);

  const fetchJobLimits = async () => {
    try {
      const response = await axios.get(`${API}/jobs/limits`);
      setJobLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch job limits:', error);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const calculateTotal = () => {
    const labor = parseFloat(formData.labor_amount) || 0;
    const materials = parseFloat(formData.materials_amount) || 0;
    return (labor + materials).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }

    const labor = parseFloat(formData.labor_amount) || 0;
    const materials = parseFloat(formData.materials_amount) || 0;
    
    if (labor === 0 && materials === 0) {
      toast.error('Please enter labor or materials amount');
      return;
    }
    
    // Validate recurring settings
    if (isRecurring && date && !recurrenceEndDate) {
      toast.error('Please select an end date for recurring appointments');
      return;
    }
    
    setLoading(true);
    
    try {
      const jobData = {
        ...formData,
        labor_amount: labor,
        materials_amount: materials,
        scheduled_date: date ? date.toISOString() : null,
        is_recurring: isRecurring && date && hasRecurring,
        recurrence_pattern: isRecurring ? recurrencePattern : null,
        recurrence_end_date: isRecurring && recurrenceEndDate ? recurrenceEndDate.toISOString() : null
      };
      
      const response = await axios.post(`${API}/jobs`, jobData);
      toast.success(isRecurring ? 'Recurring job created!' : 'Job created!');
      navigate(`/job/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create job';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const canCreateJob = !jobLimits || jobLimits.can_create_job;

  return (
    <div className="p-4 animate-fade-in" data-testid="new-job-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-heading text-2xl font-bold text-slate-dark">
          New Job
        </h1>
      </div>

      {/* Job Limit Warning */}
      {jobLimits && !jobLimits.unlimited && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          canCreateJob 
            ? 'bg-blue-50 border border-blue-200 text-blue-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="text-sm">
            {canCreateJob ? (
              <>Jobs this month: <strong>{jobLimits.jobs_this_month}</strong> of <strong>{jobLimits.max_jobs_per_month}</strong> (Free tier)</>
            ) : (
              <>Free tier limit reached. <button onClick={() => navigate('/profile')} className="underline font-bold">Upgrade</button> for unlimited jobs.</>
            )}
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Client Name */}
        <div className="space-y-2">
          <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
            Client Name *
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              name="client_name"
              data-testid="job-client-name"
              type="text"
              value={formData.client_name}
              onChange={handleChange}
              placeholder="John Doe"
              className="h-14 pl-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg rounded-lg"
              required
            />
          </div>
        </div>
        
        {/* Phone and Email Row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Phone
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="client_phone"
                data-testid="job-client-phone"
                type="tel"
                value={formData.client_phone}
                onChange={handleChange}
                placeholder="555-1234"
                className="h-12 pl-10 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="client_email"
                data-testid="job-client-email"
                type="email"
                value={formData.client_email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="h-12 pl-10 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
              />
            </div>
          </div>
        </div>
        
        {/* Address Section */}
        <div className="space-y-3">
          <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Job Address
          </Label>
          
          <Input
            name="address_street"
            data-testid="job-address-street"
            type="text"
            value={formData.address_street}
            onChange={handleChange}
            placeholder="Street Address"
            className="h-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
          />
          
          <div className="grid grid-cols-6 gap-2">
            <Input
              name="address_city"
              data-testid="job-address-city"
              type="text"
              value={formData.address_city}
              onChange={handleChange}
              placeholder="City"
              className="col-span-3 h-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
            />
            <Input
              name="address_state"
              data-testid="job-address-state"
              type="text"
              value={formData.address_state}
              onChange={handleChange}
              placeholder="State"
              className="col-span-1 h-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
            />
            <Input
              name="address_zip"
              data-testid="job-address-zip"
              type="text"
              value={formData.address_zip}
              onChange={handleChange}
              placeholder="ZIP"
              className="col-span-2 h-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg"
            />
          </div>
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
            Job Description *
          </Label>
          <div className="relative">
            <FileText className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
            <Textarea
              name="description"
              data-testid="job-description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the work to be done..."
              className="min-h-[100px] pl-12 pt-3 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg rounded-lg resize-none"
              required
            />
          </div>
        </div>
        
        {/* Labor and Materials */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold flex items-center gap-1">
              <Wrench className="w-4 h-4" />
              Labor
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="labor_amount"
                data-testid="job-labor-amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.labor_amount}
                onChange={handleChange}
                placeholder="0.00"
                className="h-12 pl-9 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg font-bold rounded-lg"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold flex items-center gap-1">
              <Package className="w-4 h-4" />
              Materials
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="materials_amount"
                data-testid="job-materials-amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.materials_amount}
                onChange={handleChange}
                placeholder="0.00"
                className="h-12 pl-9 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg font-bold rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Subtotal Display */}
        <div className="bg-slate-100 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="text-slate-600 font-medium">Subtotal (before tax)</span>
            <span className="font-heading text-2xl font-bold text-slate-900">${calculateTotal()}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Tax will be calculated based on your settings</p>
        </div>
        
        {/* Scheduled Date */}
        <div className="space-y-2">
          <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
            Scheduled Date (Optional)
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="job-date-picker"
                className="w-full h-14 px-4 bg-white border-2 border-slate-300 rounded-lg flex items-center gap-3 text-left"
              >
                <CalendarIcon className="w-5 h-5 text-slate-400" />
                <span className={date ? 'text-slate-900 text-lg' : 'text-slate-400 text-lg'}>
                  {date ? format(date, 'PPP') : 'Pick a date'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Recurring Options (Premium feature) */}
        {date && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-purple-700 uppercase text-xs tracking-wider font-bold flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Recurring Appointment
                {!hasRecurring && (
                  <span className="flex items-center gap-1 text-amber-600 normal-case font-normal text-xs">
                    <Crown className="w-3 h-3" /> Crew only
                  </span>
                )}
              </Label>
              <Switch
                checked={isRecurring}
                onCheckedChange={setIsRecurring}
                disabled={!hasRecurring}
                data-testid="recurring-toggle"
              />
            </div>
            
            {isRecurring && hasRecurring && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRecurrencePattern(opt.value)}
                      className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                        recurrencePattern === opt.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-purple-600 border border-purple-200 hover:bg-purple-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-purple-600 text-xs">End Date</Label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full h-10 px-3 bg-white border border-purple-200 rounded-lg flex items-center gap-2 text-left text-sm"
                      >
                        <CalendarIcon className="w-4 h-4 text-purple-400" />
                        <span className={recurrenceEndDate ? 'text-purple-700' : 'text-purple-400'}>
                          {recurrenceEndDate ? format(recurrenceEndDate, 'PPP') : 'Select end date'}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={recurrenceEndDate}
                        onSelect={(d) => { setRecurrenceEndDate(d); setEndDateOpen(false); }}
                        disabled={(d) => d <= (date || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                {recurrenceEndDate && (
                  <p className="text-xs text-purple-600">
                    This job will repeat {recurrencePattern} from {format(date, 'MMM d')} until {format(recurrenceEndDate, 'MMM d, yyyy')}
                  </p>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Submit Button */}
        <div className="pt-4 pb-20">
          <Button
            type="submit"
            data-testid="create-job-submit"
            disabled={loading || !canCreateJob}
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase tracking-wide text-lg rounded-lg shadow-hard active:translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : isRecurring ? 'Create Recurring Job' : 'Create Job'}
          </Button>
        </div>
      </form>
    </div>
  );
}
